/*
 * How It Works — cloned from https://dacharya.com/how-it-works/
 * Matches site palette (teal / cream / orange / navy) from About.jsx & Contact.jsx.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import {
  Phone,
  Ear,
  Users,
  BarChart3,
  Compass,
  GraduationCap,
  UserCheck,
  Sparkles,
  Target,
  Eye,
  Focus,
  ArrowRight,
  CheckCircle,
  CheckCircle2,
  Quote,
  Loader2,
  Heart,
  ShieldCheck,
  UserPlus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Palette (matches About.jsx / Contact.jsx)
const TEAL = '#0d6d64';
const TEAL_DARK = '#0a544d';
const CREAM = '#F7F1E4';
const ORANGE = '#e67e22';
const NAVY = '#1a2340';
const WHATSAPP = 'http://wa.me/+918291188188';

const HERO_TAGS = ['Smart Learning', 'Real time Updates', 'Better Outcomes'];

const GUIDE_STEPS = [
  {
    icon: Phone,
    title: 'We Connect',
    text: 'Our observer connects with your child on a call.',
  },
  {
    icon: Ear,
    title: 'We Listen & Observe',
    text: 'We listen carefully, observe and understand their thoughts and feelings.',
  },
  {
    icon: UserCheck,
    title: 'Principal Analysis',
    text: 'Our principal listens to the conversations and analyzes key insights.',
  },
  {
    icon: BarChart3,
    title: 'Data Driven Insights',
    text: 'We share daily insights and fortnightly reports with you.',
  },
  {
    icon: Compass,
    title: 'Guide & Build Path',
    text: 'Principal guides parents and personalized development path for your child.',
  },
];

const STUDENT_BENEFITS = [
  'Interactive & engaging learning',
  'Personalized support',
  'Builds confidence',
  'Better understanding',
  'Fun learning experience',
];

const PARENT_BENEFITS = [
  'Transparency in learning',
  'Regular updates',
  'Active involvement',
  'Peace of mind',
  'Stronger child development',
];

const PROCESS_STEPS = [
  {
    number: '01',
    title: 'Setup',
    text: 'Initial assessment and matching with a dedicated wellness observer based on child profile.',
  },
  {
    number: '02',
    title: 'Analysis',
    text: 'Daily 5-minute sessions translated into weekly wellness insights for parents and educators.',
  },
  {
    number: '03',
    title: 'Growth',
    text: 'Long-term behavioral tracking and emotional maturity milestones reached through consistent support.',
  },
];

const GAINS = [
  {
    icon: Sparkles,
    title: 'Better Confidence',
    text: 'Helps children express themselves with confidence and positivity.',
  },
  {
    icon: Target,
    title: 'Clarity About Strengths',
    text: 'Identifies natural talents, interests, and hidden potential.',
  },
  {
    icon: Focus,
    title: 'Improved Focus',
    text: 'Supports better concentration in studies and daily activities.',
  },
];

const TESTIMONIALS = [
  {
    quote:
      "My daughter used to keep her feelings bottled up. After 2 months with Sanjaya, she's talking about her school day with such clarity.",
    name: 'Ananya',
    role: 'Parent of 7yo',
  },
  {
    quote:
      "The reports are so insightful. I finally understand the specific anxieties my son was facing in class. It's been a game changer.",
    name: 'Rohan K.',
    role: 'Parent of 9yo',
  },
  {
    quote:
      "The 5-minute call is the highlight of my child's day. He feels like a VIP. His confidence has grown tremendously.",
    name: 'Sunita L.',
    role: 'Parent of 11yo',
  },
];

const HowItWorks = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  // Standalone plan-registration gate — same shape as Home.jsx's "Begin the
  // Journey With a Trial" section, sharing the same localStorage flag so the
  // gate stays unlocked across pages once a parent has registered.
  const [hasRegistered, setHasRegistered] = useState(() => !!localStorage.getItem('sanjaya_plan_inquiry_submitted'));
  const [planReg, setPlanReg] = useState({ parent_name: '', email: '', phone: '', child_name: '', child_age: '', school_name: '' });
  const [planRegSubmitting, setPlanRegSubmitting] = useState(false);

  const submitPlanRegistration = async (e) => {
    e.preventDefault();
    const { parent_name, email, phone, child_name, child_age } = planReg;
    if (!parent_name.trim() || !email.trim() || !phone.trim() || !child_name.trim() || !child_age) {
      toast({ title: 'Please fill in parent name, email, phone, child name and age.', variant: 'destructive' });
      return;
    }
    setPlanRegSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        parent_name: parent_name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        child_name: child_name.trim(),
        child_age: parseInt(child_age, 10) || 0,
        school_name: planReg.school_name.trim(),
        message: 'Registered from the Transparent Plans section to view pricing.',
      });
      localStorage.setItem('sanjaya_plan_inquiry_submitted', 'true');
      setHasRegistered(true);
      toast({ title: 'Registered!', description: 'Our team has your details — here are the plans.' });
    } catch (err) {
      toast({ title: 'Could not submit registration. Please try WhatsApp instead.', variant: 'destructive' });
    } finally {
      setPlanRegSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Georgia, serif' }}>
      <Navigation />

      {/* ═══════════ HERO ═══════════ */}
      <section
        className="pt-28 pb-16 px-4"
        style={{ background: CREAM }}
        data-testid="hiw-hero-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
              style={{ color: NAVY }}
              data-testid="hiw-hero-title"
            >
              How SANJAYA Works
            </h1>
            <p
              className="text-lg mb-8 leading-relaxed"
              style={{ color: '#4a5568' }}
              data-testid="hiw-hero-subtitle"
            >
              First understand the child deeply, then guide both the child and
              parents in the right direction.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              <Button
                size="lg"
                onClick={() => (window.location.href = '/contact')}
                className="rounded-full px-7 py-6 text-base text-white shadow-md"
                style={{ background: ORANGE }}
                data-testid="hiw-hero-cta-primary"
              >
                Get Started
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/#begin-trial')}
                className="rounded-full px-7 py-6 text-base border-2"
                style={{ borderColor: TEAL, color: TEAL }}
                data-testid="hiw-hero-cta-secondary"
              >
                Book a Demo
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {HERO_TAGS.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1.5 rounded-full text-sm font-medium"
                  style={{
                    background: 'rgba(13, 109, 100, 0.08)',
                    color: TEAL_DARK,
                  }}
                  data-testid={`hiw-hero-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center" data-testid="hiw-hero-image">
            <div
              className="rounded-[2rem] overflow-hidden shadow-xl w-full max-w-lg aspect-[3/2]"
              style={{ background: 'linear-gradient(135deg, #d4b896 0%, #a68968 100%)' }}
            >
              <img
                src="https://dacharya.com/wp-content/uploads/2026/05/6361688b-c330-4c45-89b3-83af000c5768-1024x683.png"
                alt="How Sanjaya works"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ HOW WE GUIDE YOUR CHILD ═══════════ */}
      <section className="py-20 px-4 bg-white" data-testid="hiw-guide-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: NAVY }}
              data-testid="hiw-guide-title"
            >
              How We Guide Your Child
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>
              We understand your child. We guide you better.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {GUIDE_STEPS.map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.title}
                  className="relative bg-white rounded-2xl p-6 border shadow-sm hover:shadow-lg transition-shadow"
                  style={{ borderColor: 'rgba(13, 109, 100, 0.15)' }}
                  data-testid={`hiw-guide-step-${idx + 1}`}
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                    style={{ background: 'rgba(230, 126, 34, 0.12)' }}
                  >
                    <Icon className="w-7 h-7" style={{ color: ORANGE }} />
                  </div>
                  <div
                    className="text-xs font-semibold mb-2 tracking-wider"
                    style={{ color: TEAL }}
                  >
                    STEP {String(idx + 1).padStart(2, '0')}
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{ color: NAVY }}>
                    {step.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: '#4a5568' }}>
                    {step.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ BENEFITS FOR EVERYONE ═══════════ */}
      <section
        className="py-20 px-4"
        style={{ background: CREAM }}
        data-testid="hiw-benefits-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: NAVY }}
              data-testid="hiw-benefits-title"
            >
              Benefits for Everyone
            </h2>
            <p className="text-lg leading-relaxed" style={{ color: '#4a5568' }}>
              Their main goal is to understand a child&apos;s behavior, emotions,
              strengths, and challenges, then guide both the child and parents
              toward better growth and development.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Students */}
            <div
              className="rounded-3xl p-8 shadow-md"
              style={{ background: 'white' }}
              data-testid="hiw-benefits-students"
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(13, 109, 100, 0.12)' }}
                >
                  <GraduationCap className="w-6 h-6" style={{ color: TEAL }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: NAVY }}>
                  For Students
                </h3>
              </div>
              <ul className="space-y-3">
                {STUDENT_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2
                      className="w-5 h-5 flex-shrink-0 mt-0.5"
                      style={{ color: TEAL }}
                    />
                    <span style={{ color: '#4a5568' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Parents */}
            <div
              className="rounded-3xl p-8 shadow-md"
              style={{ background: 'white' }}
              data-testid="hiw-benefits-parents"
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(230, 126, 34, 0.12)' }}
                >
                  <Users className="w-6 h-6" style={{ color: ORANGE }} />
                </div>
                <h3 className="text-2xl font-bold" style={{ color: NAVY }}>
                  For Parents
                </h3>
              </div>
              <ul className="space-y-3">
                {PARENT_BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <CheckCircle2
                      className="w-5 h-5 flex-shrink-0 mt-0.5"
                      style={{ color: ORANGE }}
                    />
                    <span style={{ color: '#4a5568' }}>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ 3-STEP PROFESSIONAL PROCESS ═══════════ */}
      <section
        className="py-20 px-4"
        style={{ background: NAVY }}
        data-testid="hiw-process-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-white"
              data-testid="hiw-process-title"
            >
              A Professional 3-Step Process
            </h2>
            <p className="text-lg text-white/80 leading-relaxed">
              Our principal-led methodology ensures every interaction is measured,
              meaningful, and growth-oriented.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((s) => (
              <div
                key={s.number}
                className="rounded-3xl p-8 border transition-transform hover:-translate-y-1"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderColor: 'rgba(230, 126, 34, 0.3)',
                }}
                data-testid={`hiw-process-step-${s.number}`}
              >
                <div
                  className="text-5xl font-bold mb-4 leading-none"
                  style={{ color: ORANGE }}
                >
                  {s.number}.
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{s.title}</h3>
                <p className="text-white/75 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ WHAT CHILDREN GAIN ═══════════ */}
      <section className="py-20 px-4 bg-white" data-testid="hiw-gains-section">
        <div className="max-w-6xl mx-auto">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-14"
            style={{ color: NAVY }}
            data-testid="hiw-gains-title"
          >
            What Children Gain
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {GAINS.map((g) => {
              const Icon = g.icon;
              return (
                <div
                  key={g.title}
                  className="rounded-3xl p-8 text-center border shadow-sm hover:shadow-lg transition-shadow"
                  style={{ borderColor: 'rgba(13, 109, 100, 0.15)', background: '#fafafa' }}
                  data-testid={`hiw-gain-${g.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ background: 'rgba(230, 126, 34, 0.12)' }}
                  >
                    <Icon className="w-8 h-8" style={{ color: ORANGE }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: NAVY }}>
                    {g.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: '#4a5568' }}>
                    {g.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ TESTIMONIALS ═══════════ */}
      <section
        className="py-20 px-4"
        style={{ background: CREAM }}
        data-testid="hiw-testimonials-section"
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3"
              style={{ color: NAVY }}
              data-testid="hiw-testimonials-title"
            >
              Voices of Parents
            </h2>
            <p className="text-lg" style={{ color: '#4a5568' }}>
              Real stories from the Parents.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="bg-white rounded-3xl p-7 shadow-md relative"
                data-testid={`hiw-testimonial-${i + 1}`}
              >
                <Quote
                  className="absolute top-5 right-5 w-10 h-10 opacity-15"
                  style={{ color: TEAL }}
                />
                <p
                  className="text-base leading-relaxed mb-6"
                  style={{ color: '#4a5568' }}
                >
                  {t.quote}
                </p>
                <div>
                  <div className="font-bold text-lg" style={{ color: NAVY }}>
                    {t.name}
                  </div>
                  <div className="text-sm" style={{ color: TEAL }}>
                    {t.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ BEGIN THE JOURNEY WITH A TRIAL (cloned from Home.jsx #begin-trial) ═══════════ */}
      <section className="py-20 px-4 bg-white scroll-mt-20" data-testid="hiw-inquiry-section">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }} data-testid="hiw-inquiry-title">Begin the Journey With a Trial</h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">Invest in your child&rsquo;s emotional architecture with our structured programs.</p>
        </div>
        {!hasRegistered ? (
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center" data-testid="pricing-register-gate">
            {/* LEFT: narrative + a little animated life, mirrors the Inquiry section's layout */}
            <div className="text-center md:text-left">
              <div className="relative max-w-[280px] mx-auto md:mx-0 mb-8">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1457530378978-8bac673b8062?w=800&q=80&auto=format&fit=crop"
                    alt="Young seedlings growing in bright light, symbolizing nurturing and growth"
                    className="w-full h-56 object-cover"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -top-4 -right-4 w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{ background: 'linear-gradient(135deg,#0d6d64,#1a8f80)', boxShadow: '0 12px 30px -8px rgba(13,109,100,0.5)' }}
                >
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div
                  className="absolute -bottom-3 -left-3 w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center animate-bounce"
                  style={{ animationDuration: '3s' }}
                >
                  <Heart className="w-4 h-4 text-rose-500" />
                </div>
                <div
                  className="absolute top-1/2 -right-3 w-9 h-9 rounded-xl bg-white shadow-md flex items-center justify-center animate-bounce"
                  style={{ animationDuration: '2.5s', animationDelay: '0.4s' }}
                >
                  <GraduationCap className="w-4 h-4" style={{ color: '#e67e22' }} />
                </div>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>
                Let&rsquo;s get to know your family
              </h3>
              <p className="mt-3 text-slate-600 leading-relaxed">
                A few quick details help us set up the right plan and start your child&rsquo;s free trial session.
              </p>
              <ul className="mt-6 space-y-2.5 inline-block text-left">
                {['Free trial, no card needed', 'Takes under 2 minutes', 'A Principal reviews every report'].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: TEAL }} /> {t}
                  </li>
                ))}
              </ul>
              <p className="mt-6 flex items-center justify-center md:justify-start gap-1.5 text-xs text-slate-400">
                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: TEAL }} />
                Your details are safe with us — used only to set up your child&rsquo;s trial.
              </p>
            </div>

            {/* RIGHT: the form card */}
            <div
              className="rounded-3xl overflow-hidden bg-white"
              style={{ boxShadow: '0 20px 45px -12px rgba(13,109,100,0.18)', border: '1px solid #e7f4f2' }}
            >
              <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg,#0d6d64,#22a894,#F0A820)' }} />
              <div className="p-6 sm:p-8">
                <form onSubmit={submitPlanRegistration} className="space-y-4" data-testid="plan-register-form">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Parent Name <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="Your name" value={planReg.parent_name} onChange={(e) => setPlanReg({ ...planReg, parent_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-parent-name" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Name <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="Child's name" value={planReg.child_name} onChange={(e) => setPlanReg({ ...planReg, child_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-child-name" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Email <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input type="email" placeholder="you@email.com" value={planReg.email} onChange={(e) => setPlanReg({ ...planReg, email: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-email" />
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">WhatsApp Number <span style={{ color: '#e67e22' }}>*</span></label>
                    <Input placeholder="+91" value={planReg.phone} onChange={(e) => setPlanReg({ ...planReg, phone: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-phone" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">Child&rsquo;s Age <span style={{ color: '#e67e22' }}>*</span></label>
                      <select value={planReg.child_age} onChange={(e) => setPlanReg({ ...planReg, child_age: e.target.value })} className="mt-1.5 w-full h-11 border border-slate-200 rounded-xl px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400" data-testid="plan-reg-child-age">
                        <option value="">Select</option>
                        {[5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((a) => (<option key={a} value={a}>{a}</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] font-semibold text-slate-500 uppercase">School</label>
                      <Input placeholder="School name" value={planReg.school_name} onChange={(e) => setPlanReg({ ...planReg, school_name: e.target.value })} className="mt-1.5 h-11 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-teal-500/30 focus-visible:border-teal-400" data-testid="plan-reg-school" />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={planRegSubmitting}
                    className="w-full text-white rounded-full tracking-wider text-sm font-semibold h-12 gap-2 shadow-md hover:shadow-lg transition-shadow"
                    style={{ background: 'linear-gradient(90deg,#0d6d64,#159585)' }}
                    data-testid="pricing-register-now-btn"
                  >
                    {planRegSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    REGISTER NOW
                  </Button>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border-2 rounded-3xl p-8" style={{ borderColor: '#e7f4f2' }}>
              <div className="text-[10px] tracking-[0.25em] font-semibold text-slate-500 uppercase mb-3">FLEXIBLE ENTRY</div>
              <h3 className="text-3xl font-bold" style={{ fontFamily: 'Georgia, serif', color: TEAL }}>Monthly</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Georgia, serif', color: '#1a2340' }}>Rs. 17,999</span>
                <span className="text-slate-500">/month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Daily 5-Min Wellness Call</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Daily Insight Reports</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: TEAL }} /> Direct Principal Access</li>
              </ul>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <Button className="mt-8 w-full text-white rounded-full tracking-wider text-sm font-semibold" style={{ background: TEAL }} data-testid="pricing-monthly-btn">START FREE TRIAL</Button>
              </a>
            </div>
            <div className="rounded-3xl p-8 relative shadow-xl text-white" style={{ background: 'linear-gradient(135deg,#f39c12,#e67e22)' }}>
              <div className="text-[10px] tracking-[0.25em] font-semibold uppercase mb-3 opacity-90">TRANSFORMATIONAL</div>
              <h3 className="text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Quarterly</h3>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>Rs 49,999</span>
                <span className="opacity-90">/3 month</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> All Monthly Features</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> Personalized Growth Roadmap</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> Quarterly Goal-Setting Session</li>
              </ul>
              <a href={WHATSAPP} target="_blank" rel="noopener noreferrer">
                <Button className="mt-8 w-full bg-white hover:bg-orange-50 rounded-full tracking-wider text-sm font-semibold" style={{ color: '#e67e22' }} data-testid="pricing-quarterly-btn">CHOOSE GROWTH</Button>
              </a>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorks;
